import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
  Textarea,
} from "@capacit-com/lara-law-shared-ui";
import { useToneOfVoiceCrud } from "../hooks/useToneOfVoiceCrud";
import { FC, useEffect } from "react";
import ToneOfVoice from "@/models/tone-of-voice/toneOfVoice";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

interface CreateAndEditToneOfVoiceDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  toneOfVoice: ToneOfVoice | null;
  setToneOfVoice: (toneOfVoice: ToneOfVoice | null) => void;
}

const formSchema = z.object({
  title: z.string().min(1, { message: "Udfyld titel" }),
  description: z.string().min(1, { message: "Udfyld beskrivelse" }),
  promptString: z.string().min(1, { message: "Udfyld prompt" }),
  isDefault: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

const defaultsFor = (toneOfVoice: ToneOfVoice | null): FormValues => ({
  title: toneOfVoice?.title || "",
  description: toneOfVoice?.description || "",
  promptString: toneOfVoice?.promptString || "",
  isDefault: toneOfVoice?.isDefault || false,
});

const CreateAndEditToneOfVoiceDialog: FC<
  CreateAndEditToneOfVoiceDialogProps
> = ({ isOpen, onOpenChange, toneOfVoice, setToneOfVoice }) => {
  const { handleCreate, handleUpdate, isCreating, isUpdating } =
    useToneOfVoiceCrud();

  const isEdit = toneOfVoice?.id !== undefined;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: defaultsFor(toneOfVoice),
  });

  // Re-seed on every open — the dialog stays mounted, so a discarded draft
  // would otherwise be back the next time it opens. `reset` also makes the
  // new values the pristine baseline for `isDirty`.
  useEffect(() => {
    if (isOpen) form.reset(defaultsFor(toneOfVoice));
  }, [isOpen, toneOfVoice, form]);

  const handleOpenChange = (open: boolean) => {
    if (!open) setToneOfVoice(null);
    onOpenChange(open);
  };

  const handleSubmit = async (values: FormValues) => {
    if (isEdit && toneOfVoice?.id) {
      await handleUpdate({ ...values, id: toneOfVoice.id });
    } else {
      await handleCreate(values);
    }
    onOpenChange(false);
  };

  return (
    <FormDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      isDirty={form.formState.isDirty}
      size="lg"
    >
      <DialogHeader>
        <DialogTitle>
          {isEdit ? "Rediger tone of voice" : "Opret tone of voice"}
        </DialogTitle>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="contents">
          <DialogBody className="flex flex-col gap-4">
            <Alert className="w-auto shrink-0">
              <AlertDescription className="text-sm">
                Den valgte tone påvirker al chat – også uden for Jura og
                workspaces. Test tonen grundigt, og overvej om den passer til
                målgruppen: en advokatfuldmægtig, en lejeretsekspert uden
                kendskab til politiloven, eller en studiemedhjælper?
              </AlertDescription>
            </Alert>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="shrink-0">
                  <FormLabel className="gap-1">
                    Titel <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Standard tone of voice" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex shrink-0 items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-normal">
                    Gør dette til standard tone of voice
                  </FormLabel>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="shrink-0">
                  <FormLabel className="gap-1">
                    Beskrivelse <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      className="w-full h-[80px] max-h-[80px] resize-none"
                      placeholder="Professionel og juridisk præcis formulering, egnet til notater, vurderinger og kontrakter. Klar og neutral tone uden fyld, med fokus på anvendelighed i praksis."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="promptString"
              render={({ field }) => (
                <FormItem className="flex min-h-0 flex-1 flex-col">
                  <FormLabel className="gap-1">
                    Prompt <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      className="field-sizing-fixed min-h-[200px] min-w-0 w-full flex-1 resize-none overflow-y-auto"
                      placeholder={
                        "Du er en juridisk medarbejder med høj faglighed og forståelse for juridisk præcision.\n" +
                        "Du kommunikerer professionelt, klart og respektfuldt – uden at være stiv i tonen. Dine svar skal være velstrukturerede, " +
                        "juridisk korrekte og lette at forstå for en jurist eller juridisk sagsbehandler. \nBrug altid et korrekt og neutralt sprog, " +
                        "med fokus på at forklare og anvende gældende regler, praksis og dokumentindhold. Når relevant, må du gerne foreslå mulige formuleringer eller forbedringer. \n" +
                        "Undgå overflødigt fyld, emotionel retorik og spekulation. \n" +
                        "Svarene skal kunne bruges direkte i juridisk sagsbehandling, notater eller kontraktudkast – uden behov for omskrivning."
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </DialogBody>

          <DialogFooter>
            <FormDialogCancel />
            <Button
              type="submit"
              disabled={
                !form.formState.isDirty ||
                !form.formState.isValid ||
                isCreating ||
                isUpdating
              }
            >
              {isCreating || isUpdating ? (
                <>
                  <Spinner className="w-4 h-4 text-primary-foreground mr-2" />{" "}
                  {isEdit ? "Gemmer" : "Opretter"}
                </>
              ) : isEdit ? (
                "Gem"
              ) : (
                "Opret"
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default CreateAndEditToneOfVoiceDialog;
